import { createFileRoute } from "@tanstack/react-router";
import { breadcrumbJsonLd } from "@/lib/breadcrumb-jsonld";
import { LegalLayout, COMPANY } from "@/components/site/LegalLayout";

export const Route = createFileRoute("/retourbeleid")({
  head: () => ({
    meta: [
      { title: "Retourbeleid — 14 dagen bedenktijd — Elvion Group Nederland" },
      { name: "description", content: "Retourneer eenvoudig binnen 14 dagen. Volledige uitleg over het herroepingsrecht en de werkwijze bij Elvion Group Nederland." },
      { name: "robots", content: "index,follow" },
      { property: "og:title", content: "Retourbeleid — Elvion Group Nederland" },
      { property: "og:description", content: "Lees hoe retourneren werkt: 14 dagen bedenktijd, retour aanmelden, verwerkingstijden en terugbetaling." },
      { property: "og:url", content: "https://elvion-group.nl/retourbeleid" },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
      { name: "twitter:title", content: "Retourbeleid — Elvion Group Nederland" },
      { name: "twitter:description", content: "14 dagen bedenktijd en duidelijke retourvoorwaarden bij Elvion Group Nederland." },
    ],
    links: [{ rel: "canonical", href: "https://elvion-group.nl/retourbeleid" }],
    scripts: [breadcrumbJsonLd([{ name: "Retourbeleid", path: "/retourbeleid" }])],
  }),
  component: () => (
    <LegalLayout
      title="Retourbeleid"
      intro="U heeft het recht uw bestelling tot 14 dagen na ontvangst zonder opgave van reden te annuleren. Hieronder leest u precies hoe dat werkt, welke termijnen gelden en welke uitzonderingen wij hanteren."
    >
      <h2>1. De drie retourtermijnen</h2>
      <ol>
        <li><strong>14 dagen bedenktijd</strong> — vanaf de dag na ontvangst van het product heeft u 14 kalenderdagen om uw retour bij ons aan te melden.</li>
        <li><strong>14 dagen om te retourneren</strong> — na aanmelding heeft u nogmaals 14 kalenderdagen om het product daadwerkelijk aan ons terug te sturen.</li>
        <li><strong>14 dagen terugbetaling</strong> — wij betalen het volledige aankoopbedrag (inclusief de oorspronkelijke verzendkosten) binnen 14 dagen na aanmelding van de retour terug, mits het product in goede orde retour is ontvangen.</li>
      </ol>

      <h2>2. Hoe meldt u uw retour aan</h2>
      <ol>
        <li>Meld uw retour aan via e-mail naar <a href={`mailto:${COMPANY.email}?subject=Retour%20aanmelden%20-%20ordernummer`}>{COMPANY.email}</a>, telefonisch via {COMPANY.phone}, of gebruik ons <a href="/modelformulier-herroeping">modelformulier voor herroeping</a>. U kunt ook de herroepingsknop in uw bestelbevestiging gebruiken.</li>
        <li>U ontvangt binnen <strong>1 werkdag</strong> een bevestiging met het retouradres, een retour-referentienummer en eventuele instructies.</li>
        <li>Verpak het product zorgvuldig — indien redelijkerwijs mogelijk in de originele verpakking en met alle geleverde toebehoren — en stuur het naar het retouradres met vermelding van het retour-referentienummer.</li>
        <li>Na ontvangst controleren wij het product binnen <strong>2 werkdagen</strong> en verwerken wij de terugbetaling binnen 14 dagen na aanmelding.</li>
      </ol>

      <h2>2a. Welke gegevens hebben wij nodig?</h2>
      <p>Om uw retour vlot te verwerken, vragen wij bij aanmelding de volgende gegevens:</p>
      <ul>
        <li><strong>Ordernummer</strong> (te vinden in uw bestelbevestiging, begint met <em>ORD-</em>)</li>
        <li><strong>Naam</strong> en het e-mailadres waarmee de bestelling is geplaatst</li>
        <li><strong>Artikel(en)</strong> die u wilt retourneren, inclusief aantal</li>
        <li><strong>Reden van retour</strong> (niet verplicht, wél nuttig voor onze service)</li>
        <li>Bij een defect of transportschade: een <strong>korte omschrijving en foto's</strong> van het gebrek en de verpakking</li>
        <li><strong>IBAN-rekeningnummer</strong> als het oorspronkelijke betaalmiddel niet meer beschikbaar is</li>
      </ul>

      <h2>2b. Verwachte verwerkingstijden</h2>
      <ul>
        <li><strong>Bevestiging van aanmelding:</strong> binnen 1 werkdag (ma–vr 09:00–17:00)</li>
        <li><strong>Ontvangstcontrole na binnenkomst retour:</strong> binnen 2 werkdagen</li>
        <li><strong>Terugbetaling:</strong> binnen 14 dagen na aanmelding, doorgaans binnen 3–5 werkdagen na ontvangst van het product</li>
        <li><strong>Beoordeling defect / garantie:</strong> binnen 5 werkdagen; complexe gevallen laten wij binnen die termijn met een tijdsindicatie weten</li>
      </ul>


      <h2>3. Retouradres</h2>
      <p>Retourzendingen worden alléén in behandeling genomen na aanmelding. Het retouradres is:</p>
      <p>
        <strong>{COMPANY.name}</strong><br />
        t.a.v. Retouren<br />
        {COMPANY.address}<br />
        {COMPANY.postal} Wageningen<br />
        {COMPANY.country}
      </p>

      <h2>4. Voorwaarden</h2>
      <ul>
        <li>U mag het product uitpakken en bekijken zoals u in een winkel zou doen.</li>
        <li>Het product is compleet (inclusief accessoires, handleiding en eventuele geschenken).</li>
        <li>Het product is, waar redelijkerwijs mogelijk, in de originele staat en verpakking.</li>
      </ul>

      <h2>5. Waardevermindering bij beschadiging of gebruik</h2>
      <p>U bent aansprakelijk voor waardevermindering van het product als de behandeling verder ging dan nodig was om de aard, kenmerken en werking vast te stellen. Wij mogen deze waardevermindering in dat geval verrekenen met het terug te betalen bedrag. Bij een beschadigd, incompleet of duidelijk gebruikt product kan de vergoeding daarom lager uitvallen dan het aankoopbedrag.</p>

      <h2>6. Kosten van retourneren</h2>
      <p>De directe kosten voor het terugzenden komen voor uw rekening, tenzij het product een gebrek vertoont of verkeerd is geleverd — in dat geval dragen wij de kosten. Reken op circa € 7,25 per pakket; raadpleeg voor exacte tarieven de website van uw vervoerder.</p>

      <h2>7. Terugbetaling</h2>
      <p>Wij betalen terug via dezelfde betaalmethode als waarmee u de bestelling betaalde, tenzij u uitdrukkelijk instemt met een andere methode. Voor de terugbetaling worden geen kosten in rekening gebracht.</p>

      <h2>8. Uitzonderingen op het herroepingsrecht</h2>
      <p>Het herroepingsrecht geldt niet voor:</p>
      <ul>
        <li>Op maat gemaakte, gepersonaliseerde of speciaal voor u bestelde producten</li>
        <li>Verzegelde producten die om hygiënische of gezondheidsredenen niet retour kunnen zodra de verzegeling is verbroken</li>
        <li>Software, audio- of video-opnamen waarvan de verzegeling is verbroken</li>
        <li>Producten die snel bederven of een beperkte houdbaarheid hebben</li>
        <li>Losse digitale inhoud die met uw uitdrukkelijke voorafgaande toestemming is geleverd</li>
      </ul>

      <h2>9. Modelformulier voor herroeping</h2>
      <p>U kunt ons <a href="/modelformulier-herroeping">modelformulier voor herroeping</a> downloaden, invullen en per e-mail of post naar ons terugsturen. Gebruik van het formulier is niet verplicht — een duidelijke e-mail volstaat.</p>

      <h2>10. Klachten en geschillen</h2>
      <p>Zie onze <a href="/klachten">klachtenpagina</a> voor de klachtenprocedure en geschillenbemiddeling.</p>

      <h2>Contact</h2>
      <p>{COMPANY.name} — {COMPANY.email} — {COMPANY.phone}</p>
    </LegalLayout>
  ),
});
